import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import AssignDriver from '@/components/order/AssignDriver';
import OrderStatusUpdate from '@/components/order/OrderStatus';
import Card from '@/components/UI/Card';
import DateRangePicker from '@/components/UI/DateRange';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { ActionConfig } from '@/interfaces/common.interface';
import {
  IOrderResponse,
  IOrderStates,
  Order,
} from '@/interfaces/order.interface';
import {
  formatDateTime,
  formatDeliveryRange,
  GetFilterUrl,
  getPaymentMethodMessage,
  orderFilterOption,
  OrderStatus,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useMemo, useState } from 'react';
import { Range } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

const Orders: React.FC = () => {
  const { canView, canEdit } = usePermissions();
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [state, setState] = useState<IOrderStates>({
    orderStatus: '',
    type: '',
    isJunk: null,
    order: [],
    pageInfo: {
      total: 10,
      totalPages: 1,
      limit: 10,
      page: 1,
    },
    showPaymentModel: false,
    isLoading: false,
    paymentStatus: 'PAID',
  });

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const handleChangeState = (newState: Partial<typeof state>) => {
    setState((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.order);

  useEffect(() => {
    fetchOrders();
  }, [
    state.pageInfo.page,
    state.pageInfo.limit,
    state.orderStatus,
    state.type,
    state.isJunk,
    state.paymentStatus,
    debounceValue,
    dateRange,
  ]);

  const updatePaymentStatus = async (orderId: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/update-status`;
      await callApi(PATH, {
        method: 'PATCH',
        data: { id: orderId, paymentStatus: 'PAID' },
      });
      setSelectedOrderId(null);
      showToastNotification('success', 'payment status updated to paid.');
      fetchOrders();
    } catch (error) {
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  const fetchOrders = async () => {
    try {
      const startDate = formatDateTime(dateRange[0].startDate, 'YYMMDD');
      const endDate = formatDateTime(dateRange[0].endDate, 'YYMMDD');

      const filterQuery = {
        page: state.pageInfo.page,
        limit: state.pageInfo.limit,
        status: state.orderStatus,
        paymentStatus: state.paymentStatus,
        type: state.type,
        search: debounceValue,
        startDate: startDate,
        endDate: endDate,
        isJunk: state.isJunk,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}?${query ? `${query}` : ''}`;
      const res = await callApi<IOrderResponse>(PATH, { method: 'GET' });
      handleChangeState({
        order: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      handleChangeState({ isLoading: false });
    }
  };

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...state.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...state.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  const handleFilterChange = (filter: string) => {
    const paymentStatuses = ['PAID', 'UNPAID'];

    let newState: Partial<IOrderStates> = {
      orderStatus: '',
      paymentStatus: '',
      isJunk: null,
      pageInfo: { ...state.pageInfo, page: 1 },
    };

    if (paymentStatuses.includes(filter)) {
      newState.paymentStatus = filter;
    } else if (filter === 'isJunk') {
      newState.isJunk = true;
    } else {
      newState.orderStatus = filter;
      newState.paymentStatus = 'PAID';
    }

    handleChangeState(newState);
  };

  const orderColumns: TableColumn<Order>[] = [
    {
      key: 'orderId',
      header: 'Order ID',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.orderId}
        </span>
      ),
      className: 'p-3',
    },
    {
      key: 'dateTime',
      header: 'Date/Time',
      render: (order) => {
        const deliveredInfo = order?.previousStatus?.find(
          (v) => v.status === OrderStatus.DELIVERED,
        );
        const date = deliveredInfo?.timestamp
          ? formatDateTime(deliveredInfo?.timestamp, 'YYMMMDDHH')
          : formatDeliveryRange(order?.formattedDeliveryTime);
        return (
          <span
            className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}
          >
            {date}
          </span>
        );
      },
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.personalInfo.firstName} {order.personalInfo.lastName}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total Amount BD',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {toFixedThree(order.grandTotal)}
        </span>
      ),
    },
    {
      key: 'orderStatus',
      header: 'Order Status',
      render: (order) => (
        <OrderStatusUpdate order={order} onSucess={() => fetchOrders()} />
      ),
    },
    {
      key: 'paymentType',
      header: 'Payment Type',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {getPaymentMethodMessage(
            order.paymentMethod?.type,
            order.paymentStatus,
          ) ?? 'N/A'}
        </span>
      ),
    },
    // {
    //   key: 'deliveryStatus',
    //   header: 'Delivery Status',
    //   render: (order) => order.deliveryStatus,
    // },
    {
      key: 'items',
      header: 'Items',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.orderItems?.length ?? 0}
        </span>
      ),
    },
    {
      header: 'Payment Status',
      key: 'paymentStatus',
      render: (item) => {
        return (
          state.order && (
            <StatusDropdown
              value={item?.paymentStatus}
              options={
                item?.paymentStatus !== 'PAID'
                  ? [
                      {
                        value: 'PAID',
                        label: 'Paid',
                        textClass: 'text-blue-500',
                        bgClass: 'bg-blue-500',
                      },
                    ]
                  : []
              }
              onChange={() => {
                handleChangeState({ showPaymentModel: true });
                setSelectedOrderId(item.id);
              }}
              canEditVal="order"
            />
          )
        );
      },
    },
  ];

  const orderActionConfig: Record<string, ActionConfig<Order>> = {
    view: {
      text: 'View',
      onClick: (order) => navigate(`${RoutesLink.orderDetail}/${order.id}`),
      permission: () => canView('order'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(orderActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit],
  );

  const filterOptions = [
    { value: 'customers', label: 'Customers' },
    { value: 'employee', label: 'Employees' },
  ];

  return (
    <Layout>
      <PageHeading text="Orders" />
      <Card>
        <div className=" flex flex-col lg:flex-row w-full mb-4 items-start flex-wrap lg:items-center justify-between gap-3 ">
          <CommonFilter
            filters={orderFilterOption}
            onChange={handleFilterChange}
            inputPlaceHolder="Search for Orders..."
            onSearch={(text) => setSearchValue(text)}
            filterOptions={{
              values: filterOptions,
              onChange: (value) => handleChangeState({ type: value }),
            }}
          />
          <div className="flex items-center gap-3 flex-1">
            <div className="">
              <DateRangePicker
                dateRange={dateRange}
                setDateRange={setDateRange}
              />
            </div>
            {state.order.length !== 0 && (
              <ExportOption
                APIPath={`${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/download-report`}
                dateRange={dateRange[0]}
                status={state.orderStatus}
              />
            )}
          </div>
        </div>
        <CommonTable
          columns={orderColumns}
          data={state.order}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Order found"
          loading={state.isLoading}
          actions={actions}
        />
        <Pagination
          isLoading={state.isLoading}
          paginationData={state.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
        />
        {isModalOpen && selectedOrderId && (
          <AssignDriver
            id={selectedOrderId}
            setIsOpen={setIsModalOpen}
            onSuccess={() => {
              (fetchOrders(), setSelectedOrderId(null));
            }}
          />
        )}
        {state.showPaymentModel && (
          <>
            <ConfirmationModel
              modelType="update"
              show={state.showPaymentModel}
              onClose={() => {
                handleChangeState({ showPaymentModel: false });
                setSelectedOrderId(null);
              }}
              onConfirm={() => updatePaymentStatus(String(selectedOrderId))}
              type={`Paid`}
              subTitle="Are you sure you want to update payment status to"
            />
          </>
        )}
      </Card>
    </Layout>
  );
};

export default Orders;
